/**
 * CourseDetailPageTab
 *
 * Tab 2 of the course editor — customizes the marketing/about page learners
 * see BEFORE they enroll: hero image, sample video, CE information, and
 * the content tabs (Overview, Resources, etc.) that render on the detail
 * page.
 *
 * Owns its own state + Save button. Sends an updateCourse payload merged
 * with the loaded course so the other tabs' fields (Settings, Curriculum)
 * aren't clobbered. Sample video URL + CE Info persist on the course
 * settings blob alongside the rest of the save.
 */

import { useSnackbar } from 'notistack';
import { useEffect, useState } from 'react';
import { useDispatch } from 'react-redux';
import { Course, CourseDetailTab, CourseType } from '../../apis/api-client';
import { coursesClient } from '../../apis/client-instances';
import { updateCourse } from '../../features/courses/coursesSlice';
import { alert } from '../../hooks/function';
import { getSampleVideoEnabled, getSampleVideoUrl } from '../../utils/coursePrefs';
import {
  CourseCEInfo,
  EMPTY_CE_INFO,
  getCEInfo,
} from '../../utils/courseCEInfo';
import { courseToSettings } from '../../utils/courseSettings';
import CourseCEInfoSection from './CourseCEInfoSection';
import CourseDetailPageSection from './CourseDetailPageSection';
import CourseDetailTabsEditor from './CourseDetailTabsEditor';
import {
  Box,
  Button,
  Card,
  CircularProgress,
  FormControlLabel,
  Switch,
  TextField,
  Typography,
} from '../ui';

export interface CourseDetailPageTabProps {
  course: Course | null;
  /** Parent re-fetches the course so subsequent tab visits see the updates
   *  (especially the detail tabs, where the server reconciles ids). */
  onCourseChanged?: () => void;
}

export default function CourseDetailPageTab({ course, onCourseChanged }: CourseDetailPageTabProps) {
  const dispatch = useDispatch();
  const { enqueueSnackbar } = useSnackbar();

  const [detailImageUrl, setDetailImageUrl] = useState('');
  const [detailTabs, setDetailTabs] = useState<CourseDetailTab[]>([]);
  const [saving, setSaving] = useState(false);

  // Sample / preview video for the learner course-detail hero, and the
  // CE Information block. Both lived on the Settings tab previously;
  // they read more naturally beside the rest of the learner-facing
  // detail-page authoring, so we host them here.
  const [sampleVideoUrlState, setSampleVideoUrlState] = useState('');
  const [sampleVideoEnabledState, setSampleVideoEnabledState] = useState(true);
  // Starts empty — CE fields are authored per course, nothing is
  // pre-filled. Blank fields persist blank and stay off the learner tab.
  const [ceInfoState, setCEInfoState] = useState<CourseCEInfo>({ ...EMPTY_CE_INFO });
  const [ceInfoEnabledState, setCEInfoEnabledState] = useState(true);
  // CE Hours — a scalar on the course record (not part of the CE Info
  // blob). Held as a string so the number field can be cleared; parsed
  // on save. Feeds the catalog card badge + the certificate CeHours
  // token + the learner CE tab stat.
  const [ceHoursState, setCeHoursState] = useState('');

  // Hydrate local state whenever the loaded course changes. We deliberately
  // do NOT depend on individual course fields — only on the course id — so
  // typing in the form doesn't fight an effect-driven reset.
  useEffect(() => {
    if (!course) return;
    // Default the detail image to the course thumbnail when no explicit
    // detail image has been set yet — most courses end up using the same
    // image for both, so the field is pre-filled instead of starting empty.
    // The admin can replace it in this tab to use a different image.
    setDetailImageUrl(course.detailImageUrl || course.thumbnail || '');
    setDetailTabs((course.detailTabs as CourseDetailTab[] | undefined)?.map((t) => ({ ...t })) || []);
    // Detail-video URL + visibility read off the loaded course; writes
    // ride the settings payload below.
    setSampleVideoUrlState(getSampleVideoUrl(course));
    setSampleVideoEnabledState(getSampleVideoEnabled(course));
    setCEInfoState(getCEInfo(course.ceInfo));
    setCEInfoEnabledState(course.ceInfoEnabled ?? true);
    setCeHoursState(course.ceHours != null ? String(course.ceHours) : '');
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [course?.slug]);

  if (!course) {
    return (
      <Box sx={{ p: 3 }}>
        <Typography color="text.secondary">
          Save the course settings first to customize the detail page.
        </Typography>
      </Box>
    );
  }

  // Events render a simplified landing page (event details + Register +
  // attendance verification) — not the customizable detail page courses
  // and learning paths use. The page is fully driven by the Settings
  // tab; nothing to author here.
  if (course.courseType === CourseType.External) {
    return (
      <Box sx={{ p: 3 }}>
        <Typography variant="body1" fontWeight={600} sx={{ mb: 1 }}>
          Events use a simplified landing page
        </Typography>
        <Typography color="text.secondary">
          Configure the event URL, date, and attendance verification
          (confirmation code, post-event attestation) on the{' '}
          <strong>Settings</strong> tab. The learner page is composed
          from those fields automatically.
        </Typography>
      </Box>
    );
  }

  const handleSave = async () => {
    if (!course?.id) return;
    setSaving(true);
    try {
      // Negative tab ids are local-only (drag-reorder stability). Server
      // treats id===0 as "create".
      const tabsForSave = detailTabs.map((t) => ({
        id: t.id != null && t.id > 0 ? t.id : 0,
        title: t.title || '',
        contentHtml: t.contentHtml,
      }));

      // Merge with the loaded course so we don't clobber settings/curriculum
      // fields owned by other tabs. The legacy Wistia/Mux detail-video
      // fields are gone — Sample video below replaces them.
      const payload = {
        ...course,
        detailImageUrl,
        // Preserve any previously-saved alt text — the UI no longer
        // edits it, but we don't want to clobber existing values.
        detailImageAltText: course.detailImageAltText,
        detailTabs: tabsForSave,
        // CE Information, its visibility toggle, and the detail-video URL
        // all persist on the course settings blob. Rebuild the full
        // settings object from the loaded course so we don't clobber
        // fields other tabs own. detailVideoUrl is sent as-is (empty
        // string clears it).
        settings: {
          ...courseToSettings(course),
          // Persist exactly what the admin authored — no defaults are
          // injected, so a blank field saves blank and hasCEInfo() stays honest.
          ceInfo: ceInfoState,
          ceInfoEnabled: ceInfoEnabledState,
          detailVideoUrl: sampleVideoUrlState.trim(),
          detailVideoUrlEnabled: sampleVideoEnabledState,
          // Blank clears the value (omitted key → server sets null).
          ceHours:
            ceHoursState.trim() !== '' && Number.isFinite(Number(ceHoursState))
              ? Number(ceHoursState)
              : undefined,
        },
      };

      await coursesClient.updateCourse(course.id, payload as any);
      dispatch(updateCourse({ id: course.id, course: payload as any }));

      alert(enqueueSnackbar, 'Detail page saved', 'success');
      onCourseChanged?.();
    } catch {
      alert(enqueueSnackbar, 'Failed to save detail page', 'error');
    } finally {
      setSaving(false);
    }
  };

  return (
    <Box sx={{ display: 'flex', flexDirection: 'column', gap: 3 }}>
      <Box>
        <Typography variant="h6" fontWeight={700}>
          Detail page
        </Typography>
        <Typography variant="body2" color="text.secondary">
          What learners see before they enroll. Hero image, sample video,
          CE information, and content tabs (Overview, Resources, etc.)
          that show up on the public detail page.
        </Typography>
      </Box>

      {/* ── Detail Image + Sample Video ─────────────────────────────
          Two-column row so the visual assets sit side by side and the
          page stays compact. `auto-fit` stacks them on narrow widths;
          `alignItems: flex-start` keeps each card sized to its own
          content. */}
      <Box
        sx={{
          display: 'grid',
          gridTemplateColumns: 'repeat(auto-fit, minmax(320px, 1fr))',
          gap: 2.5,
          alignItems: 'flex-start',
        }}
      >
        <Card sx={{ p: 3 }}>
          <Box sx={{ mb: 2 }}>
            <Typography variant="h6" fontWeight={700}>
              Detail image
            </Typography>
            <Typography variant="body2" color="text.secondary">
              Hero shown on the public detail page when no sample
              video is set. Suggested size 1600×900.
            </Typography>
          </Box>
          <CourseDetailPageSection
            detailImageUrl={detailImageUrl}
            onDetailImageUrlChange={setDetailImageUrl}
          />
        </Card>

        {/* ── Sample / Preview Video ────────────────────────────────
            Optional short video that plays in the learner's course
            detail hero in place of the thumbnail still. Toggle
            decouples the URL field from learner-visibility so admins
            can stage a video without exposing it. */}
        <Card sx={{ p: 3 }}>
          <Box sx={{ display: 'flex', alignItems: 'flex-start', gap: 2, mb: 2 }}>
            <Box sx={{ flex: 1, minWidth: 0 }}>
              <Typography variant="h6" fontWeight={700}>
                Sample video
              </Typography>
              <Typography variant="body2" color="text.secondary">
                Optional short preview learners see in the course
                detail hero. When set and enabled, the video replaces
                the thumbnail still.
              </Typography>
            </Box>
            <FormControlLabel
              control={
                <Switch
                  checked={sampleVideoEnabledState}
                  onChange={(e) => setSampleVideoEnabledState(e.target.checked)}
                />
              }
              label={sampleVideoEnabledState ? 'Visible' : 'Hidden'}
              labelPlacement="start"
              sx={{ ml: 0, flexShrink: 0 }}
            />
          </Box>
          <TextField
            value={sampleVideoUrlState}
            onChange={(e) => setSampleVideoUrlState(e.target.value)}
            placeholder="https://…/preview.mp4 or a YouTube / Vimeo link"
            fullWidth
            size="small"
            disabled={!sampleVideoEnabledState}
            helperText={
              sampleVideoEnabledState
                ? 'Direct video URLs render with a native player. YouTube and Vimeo links embed as an iframe.'
                : 'Toggle on to show the sample video on the learner detail page.'
            }
          />
        </Card>
      </Box>

      {/* ── CE Information ──────────────────────────────────────────
          Drives the learner-facing "CE Information" tab. The toggle
          hides the tab entirely without clearing the field data. */}
      <Card sx={{ p: 3 }}>
        <Box sx={{ display: 'flex', alignItems: 'flex-start', gap: 2, mb: 2 }}>
          <Box sx={{ flex: 1, minWidth: 0 }}>
            <Typography variant="h6" fontWeight={700}>
              CE Information
            </Typography>
            <Typography variant="body2" color="text.secondary">
              AGD subject code, PACE provider, educational method,
              accreditation &amp; disclosure boilerplate. Turning the
              toggle off hides the CE Information tab on the learner
              detail page without clearing these fields.
            </Typography>
          </Box>
          <FormControlLabel
            control={
              <Switch
                checked={ceInfoEnabledState}
                onChange={(e) => setCEInfoEnabledState(e.target.checked)}
              />
            }
            label={ceInfoEnabledState ? 'Visible' : 'Hidden'}
            labelPlacement="start"
            sx={{ ml: 0, flexShrink: 0 }}
          />
        </Box>
        {/* CE Hours — a plain scalar on the course, independent of the
            visibility toggle: it still stamps the certificate and shows
            on the catalog card even when the CE Information tab is
            hidden. Sits above the toggle-gated fields for that reason. */}
        <TextField
          label="CE Hours"
          type="number"
          value={ceHoursState}
          onChange={(e) => setCeHoursState(e.target.value)}
          placeholder="e.g. 2"
          size="small"
          inputProps={{ min: 0, step: 0.25 }}
          helperText="Credit hours awarded. Shows on the course card and the certificate; leave blank to hide."
          sx={{ maxWidth: 260, mb: 2 }}
        />
        <Box sx={{ opacity: ceInfoEnabledState ? 1 : 0.55 }}>
          <CourseCEInfoSection value={ceInfoState} onChange={setCEInfoState} />
        </Box>
      </Card>

      {/* ── Custom tabs ─────────────────────────────────────────────
          Admin-authored stacked sections that render on the learner
          detail page. Lives at the bottom of the page because it's
          the most heavyweight authoring step and most admins won't
          touch it on every save. */}
      <Card sx={{ p: 3 }}>
        <Box sx={{ mb: 2 }}>
          <Typography variant="h6" fontWeight={700}>
            Custom tabs
          </Typography>
          <Typography variant="body2" color="text.secondary">
            Add stacked sections (Overview, Resources, etc.) that
            render on the public detail page. Drag to reorder.
          </Typography>
        </Box>
        <CourseDetailTabsEditor tabs={detailTabs} onTabsChange={setDetailTabs} />
      </Card>

      <Box sx={{ display: 'flex', justifyContent: 'flex-end', mt: 1, pt: 3, borderTop: 1, borderColor: 'divider' }}>
        <Button
          onClick={handleSave}
          variant="contained"
          color="primary"
          disabled={saving}
          startIcon={saving ? <CircularProgress size={20} color="inherit" /> : null}
        >
          {saving ? 'Saving...' : 'Save Detail Page'}
        </Button>
      </Box>
    </Box>
  );
}
